/**
 * Bloque de indicadores en tiempo real (snapshot) para listas.
 * Muestra una fila de tarjetas con valor + etiqueta, sin historial ni sparklines.
 * Usar encima de ListView con contentAboveCard cuando los datos son estado actual
 * (ej. mesas: total, libres, ocupadas) en lugar de KPIs con tendencia.
 *
 * Uso: contentAboveCard={<StatsSnapshotBar items={[...]} />}
 * Con ListView: contentAboveCardInCard={false} para evitar fondo blanco exterior.
 */

import { Card, Box, BlockStack, Text, InlineGrid } from '@/lib/polaris';

export type StatsSnapshotTone = 'success' | 'critical' | 'caution' | 'subdued' | 'info';

/**
 * Polaris `Text` no tiene tono "info"; se mapea al color base (undefined), que es
 * exactamente cómo lo renderizaba en runtime (Text ignora tonos desconocidos).
 */
const TEXT_TONE: Record<StatsSnapshotTone, 'success' | 'critical' | 'caution' | 'subdued' | undefined> = {
  success: 'success',
  critical: 'critical',
  caution: 'caution',
  subdued: 'subdued',
  info: undefined,
};

export interface StatsSnapshotItem {
  /** Etiqueta bajo el valor (ej. "Total", "Libres"). */
  label: string;
  /** Valor mostrado (número o texto). */
  value: number | string;
  /** Tono opcional del valor (colores Polaris). */
  tone?: StatsSnapshotTone;
}

export interface StatsSnapshotBarProps {
  /** Ítems a mostrar (cada uno = una tarjeta). */
  items: StatsSnapshotItem[];
  /** Columnas del grid. Por defecto { xs: 2, sm: 5 } para 2 en móvil y 5 en desktop. */
  columns?: { xs?: number; sm?: number; md?: number; lg?: number };
  /** Espaciado entre tarjetas. Por defecto "400". */
  gap?: '200' | '300' | '400' | '500';
}

export function StatsSnapshotBar({
  items,
  columns = { xs: 2, sm: 5 },
  gap = '400',
}: StatsSnapshotBarProps) {
  if (items.length === 0) return null;

  return (
    <InlineGrid columns={columns} gap={gap}>
      {items.map((item, index) => (
        <Card key={item.label + String(index)}>
          <Box padding="400">
            <BlockStack gap="100" inlineAlign="center">
              <Text
                as="p"
                variant="heading2xl"
                tone={item.tone ? TEXT_TONE[item.tone] : undefined}
              >
                {typeof item.value === 'number' ? item.value : item.value}
              </Text>
              <Text as="p" tone="subdued">
                {item.label}
              </Text>
            </BlockStack>
          </Box>
        </Card>
      ))}
    </InlineGrid>
  );
}
